import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Bed,
  Square,
  MapPin,
  Building2,
  Castle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useFunctionContext } from "../../../context/FunctionalContext";

const FeaturedListings = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { listings, listLoading } = useFunctionContext();
  const totalItems = listings?.length || 0;

  const visibleCount = 10;

  // Auto-slide every 3 seconds
  useEffect(() => {
    if (!totalItems) return;
    const interval = setInterval(() => {
      next();
    }, 3000);
    return () => clearInterval(interval);
  }, [totalItems]);

  const next = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === listings.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? listings.length - 1 : prevIndex - 1
    );
  };

  const renderListings = (count) => {
    // 👇 Show loading skeleton while fetching
    if (listLoading || !listings || listings.length === 0) {
      return <LoadingCard count={count} />;
    }

    return [...Array(count)].map((_, i) => {
      const list = listings[(currentIndex + i) % listings.length];
      return <PropertyCard key={i} list={list} />;
    });
  };

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 md:mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gold mb-3">
              Featured Listings
            </h2>
            <p className="text-gray-400 max-w-lg">
              Discover our handpicked selection of exceptional properties from
              around the world.
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Link
              to="/realty/listings"
              className="rounded-lg bg-gradient text-slate-900 px-4 py-2 font-medium hover:text-opacity-80 inline-flex items-center gap-2 transition-colors"
            >
              View All Properties →
            </Link>
          </div>
        </div>

        <div className="relative">
          {/* Responsive Cards */}
          <div className="grid grid-cols-1 gap-6 md:hidden">
            {renderListings(Math.min(1, listings.length || visibleCount))}
          </div>
          <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-6">
            {renderListings(Math.min(2, listings.length || visibleCount))}
          </div>
          <div className="hidden lg:grid lg:grid-cols-3 gap-8">
            {renderListings(Math.min(3, listings.length || visibleCount))}
          </div>

          {/* Navigation Arrows */}
          {listings?.length > 0 && !listLoading && (
            <>
              <button
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-slate-800 text-white p-2 rounded-full hover:bg-slate-700 transition shadow"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-slate-800 text-white p-2 rounded-full hover:bg-slate-700 transition shadow"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedListings;

// ----------------------------------

const PropertyCard = ({ list }) => {
  return (
    <div className="bg-slate-900/60 rounded-lg overflow-hidden shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="relative aspect-[5/3] overflow-hidden">
        {list?.images?.[0]?.url && (
          <img
            src={list.images[0].url}
            alt="img"
            className="object-cover object-center w-full h-full transition-transform duration-500 hover:scale-110"
          />
        )}
        <div className="absolute top-4 left-4 bg-black text-white px-3 py-1 rounded-md font-medium text-sm flex items-center gap-1">
          <Castle size={15} className="text-gold" /> {list.type}
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold line-clamp-1 capitalize">
            {list.name}
          </h3>
          <span className="text-gold font-bold text-nowrap">
            {list.price.toLocaleString()} {""}
            {list.currency_symbol}
          </span>
        </div>

        <div className="flex items-start text-gray-400 mb-4">
          <MapPin size={16} className="mr-1" />
          <span className="text-sm leading-tight">
            {list.location.address}, {list.location.city}, {list.location.state}
          </span>
        </div>

        <div className="flex items-center justify-between border-t border-gray-700 pt-4">
          <div className="flex items-center text-gray-400">
            <Bed size={18} className="mr-1" />
            <span className="text-sm">{list.bedrooms} Beds</span>
          </div>

          <div className="flex items-center text-gray-400">
            <Square size={18} className="mr-1" />
            <span className="text-sm">{list.area_sqft} sq ft</span>
          </div>
        </div>
      </div>

      <div className="px-5 pb-5">
        <Link
          to={`/realty/listings/${list.listing_id}`}
          className="block text-center bg-gradient hover:bg-gray-200 text-slate-900 font-medium py-2 rounded transition-colors"
        >
          View Details
        </Link>
      </div>
    </div>
  );
};

// ----------------------------------

const LoadingCard = ({ count = 3 }) =>
  Array.from({ length: count }).map((_, index) => (
    <div
      key={index}
      className="bg-gray-800 rounded-xl p-4 animate-pulse space-y-4"
    >
      <div className="bg-gray-700 w-14 h-6 rounded-md" />
      <div className="bg-gray-700 h-48 w-full rounded-md" />
      <div className="flex justify-between items-center">
        <div className="bg-gray-700 w-24 h-5 rounded" />
        <div className="bg-gray-700 w-16 h-5 rounded" />
      </div>
      <div className="bg-gray-700 w-32 h-4 rounded" />
      <div className="border-t border-[#2a2e39]" />
      <div className="flex justify-between text-sm">
        <div className="bg-gray-700 w-16 h-4 rounded" />
        <div className="bg-gray-700 w-16 h-4 rounded" />
        <div className="bg-gray-700 w-16 h-4 rounded" />
      </div>
      <div className="bg-gradient-to-r from-white to-black/80 w-full h-10 rounded-md" />
    </div>
  ));